import { useState, useCallback, useContext } from "react";
import {
  CustomField,
  CustomFieldService,
  CustomFieldDataType,
} from "./customFields.service";
import { AuthContext } from "../../../context/authcontext";

interface UseCustomFieldsState {
  customFields: CustomField[];
  loading: boolean;
  backgroundLoading: boolean;
  error: string | null;
  isInitialized: boolean;
}

export const useCustomFields = () => {
  const { selectedTenantId } = useContext(AuthContext);
  
  const [state, setState] = useState<UseCustomFieldsState>({
    customFields: [],
    loading: false,
    backgroundLoading: false,
    error: null,
    isInitialized: false,
  });

  const [dataTypes, setDataTypes] = useState<CustomFieldDataType[]>([]);
  const [dataTypesLoading, setDataTypesLoading] = useState(false);

  const fetchCustomFields = useCallback(
    async (filters?: Record<string, any>) => {
      if (!selectedTenantId) {
        setState(prev => ({
          ...prev,
          error: "No tenant selected",
          loading: false,
        }));
        return;
      }

      setState(prev => ({
        ...prev,
        loading: !prev.isInitialized,
        backgroundLoading: prev.isInitialized,
        error: null,
      }));

      try {
        const response: CustomField[] = await CustomFieldService.getCustomFieldsByPage(
          selectedTenantId,
          filters
        );

        setState(prev => ({
          ...prev,
          customFields: response,
          loading: false,
          backgroundLoading: false,
          error: null,
          isInitialized: true,
        }));
      } catch (error: any) {
        setState(prev => ({
          ...prev,
          error: error.message || "Failed to fetch custom fields",
          loading: false,
          backgroundLoading: false,
        }));
      }
    },
    [selectedTenantId]
  );

  const fetchDataTypes = useCallback(async () => {
    setDataTypesLoading(true);
    try {
      const types = await CustomFieldService.getCustomFieldDataTypes();
      setDataTypes(Array.isArray(types) ? types : []);
    } catch (error) {
      setDataTypes([]);
    } finally {
      setDataTypesLoading(false);
    }
  }, []);

  const addCustomField = useCallback((newField: CustomField) => {
    setState(prev => {
      const updatedFields = [...prev.customFields, newField].sort((a, b) => {
        const orderA = a.display_order || 0;
        const orderB = b.display_order || 0;
        return orderA - orderB;
      });
      
      return {
        ...prev,
        customFields: updatedFields,
      };
    });
  }, []);

  const updateCustomField = useCallback((updatedField: CustomField) => {
    setState(prev => {
      const updatedFields = prev.customFields.map(field =>
        field.custom_field_id === updatedField.custom_field_id ? updatedField : field
      ).sort((a, b) => {
        const orderA = a.display_order || 0;
        const orderB = b.display_order || 0;
        return orderA - orderB;
      });
      
      return {
        ...prev,
        customFields: updatedFields,
      };
    });
  }, []);

  const removeCustomField = useCallback((fieldId: number) => {
    setState(prev => ({
      ...prev,
      customFields: prev.customFields.filter(field => field.custom_field_id !== fieldId),
    }));
  }, []);

  const clearCustomFieldsData = useCallback(() => {
    setState({
      customFields: [],
      loading: false,
      backgroundLoading: false,
      error: null,
      isInitialized: false,
    });
  }, []);

  const clearBackgroundLoading = useCallback(() => {
    setState(prev => ({
      ...prev,
      backgroundLoading: false,
    }));
  }, []);

  return {
    customFields: state.customFields,
    loading: state.loading,
    backgroundLoading: state.backgroundLoading,
    error: state.error,
    isInitialized: state.isInitialized,
    dataTypes,
    dataTypesLoading,
    fetchCustomFields,
    fetchDataTypes,
    addCustomField,
    updateCustomField,
    removeCustomField,
    clearCustomFieldsData,
    clearBackgroundLoading,
  };
};
